import type { KYCQuestionDefinition } from '@anchorage/graphql/generated';
import { parseSanitize } from '@anchorage/utils';
import { CardListItem } from '@latitude/card-list-item';
import type { RadioButtonProps } from '@latitude/form';
import { FormRadioButtonGroup, reactHookForm } from '@latitude/form';
import { Text } from '@latitude/text';
import classnames from 'classnames';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';

import css from './styles.module.css';

type Props = {
  question: KYCQuestionDefinition;
  isDisabled?: boolean;
  columns?: 2 | 3;
  trackFn?: (questionId: string) => void;
};

const CardListComponent: FC<Props> = ({
  question,
  isDisabled = false,
  columns = 2,
  trackFn = () => {},
}) => {
  const { control } = reactHookForm.useFormContext();

  const cardListClasses = classnames(
    css.cardList,
    columns === 2 && css.cardList2Columns,
    columns === 3 && css.cardList3Columns,
  );

  const options = useMemo(
    () =>
      question.options?.map((opt) => ({
        value: opt.id,
        title: opt.text,
        message: (
          <Text
            variant="captionMedium"
            data-testid="page-subtitle"
            className="overflow-auto whitespace-break-spaces"
          >
            {parseSanitize(opt.subtext)}
          </Text>
        ),
        disabled: isDisabled,
      })),
    [question, isDisabled],
  );

  return (
    <FormRadioButtonGroup
      control={control}
      name={`${question.id}.options`}
      options={options ?? []}
      wrapperClassname={cardListClasses}
      disabled={isDisabled}
      render={(
        option: {
          value: string;
          title: string;
          message: ReactNode | string;
        },
        { checked, onValueChange, disabled, invalid, onBlur }: RadioButtonProps,
      ) => {
        let cardState: 'selected' | 'disabled' | 'error' | 'enabled';
        if (checked) {
          cardState = 'selected';
        } else if (disabled) {
          cardState = 'disabled';
        } else if (invalid) {
          cardState = 'error';
        } else {
          cardState = 'enabled';
        }

        return (
          <CardListItem
            state={cardState}
            onClick={
              disabled
                ? undefined
                : () => {
                    trackFn?.(question.id);
                    onValueChange?.(option.value);
                  }
            }
            onBlur={onBlur}
            title={option.title}
            subtitle={option.message}
            className="h-full items-start [&_ul]:list-disc [&_ul]:pl-4"
          />
        );
      }}
    />
  );
};

export { CardListComponent };
